import { ConfirmModal } from '@/components/ui/Modal'
import { Spinner } from '@/components/ui/Spinner'
import { ROUTES } from '@/constants/routes'
import {
  Box,
  Heading,
  HStack,
  IconButton,
  useDisclosure
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { FiArrowLeft, FiTrash2 } from 'react-icons/fi'
import { useNavigate, useParams } from 'react-router-dom'
import { LeadForm } from '../components/LeadForm'
import { useDeleteLead, useLead, useUpdateLead } from '../hooks/useLeads'

/**
 * Lead Edit Page
 * Edit lead details
 */
export function LeadEdit() {
  const { t } = useTranslation()
  const { uuid } = useParams()
  const navigate = useNavigate()
  const { open: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()

  // Fetch lead data
  const { data, isLoading, error } = useLead(uuid)
  const lead = data?.data

  // Update mutation
  const updateMutation = useUpdateLead()

  // Delete mutation
  const deleteMutation = useDeleteLead()

  const handleUpdate = async (data) => {
    try {
      await updateMutation.mutateAsync({ uuid, data })
    } catch (error) {
      // If validation error, re-throw to form for field error handling
      if (error?.error?.details && typeof error.error.details === 'object') {
        throw error
      }
      // Other errors are already handled by useUpdateLead hook
    }
  }

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(uuid)
      // Delay navigation to allow toast to be visible
      setTimeout(() => {
        navigate(ROUTES.LEADS)
      }, 1500)
    } catch (error) {
      // Error toast is already handled by useDeleteLead hook
    }
  }

  const handleCancel = () => {
    navigate(ROUTES.LEADS)
  }

  if (isLoading) {
    return <Spinner fullScreen />
  }

  if (error) {
    return (
      <Box>
        <Heading size="lg" color="red.500">
          Error loading lead
        </Heading>
        <Box mt={ 4 }>{ error.message }</Box>
      </Box>
    )
  }

  if (!lead) {
    return (
      <Box>
        <Heading size="lg">Lead not found</Heading>
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <HStack justify="space-between" mb={ 6 }>
        <HStack spacing={ 3 }>
          <IconButton
            onClick={ () => navigate(ROUTES.LEADS) }
            variant="ghost"
            size="md"
            aria-label="Back to leads"
            borderRadius="sm"
          >
            <Box as={ FiArrowLeft } boxSize="20px" />
          </IconButton>
          <Heading size="lg" color="#172B4D">
            {t('leads.editLead')}
          </Heading>
        </HStack>

        <HStack spacing={ 2 }>
          <IconButton
            variant="outline"
            size="md"
            onClick={ onDeleteOpen }
            aria-label="Delete lead"
            borderRadius="sm"
            borderColor="#DE350B"
            color="#DE350B"
            _hover={{ bg: '#FFEBE6', borderColor: '#BF2600' }}
          >
            <Box as={ FiTrash2 } boxSize="20px" />
          </IconButton>
        </HStack>
      </HStack>

      {/* Content */}
      <Box
        bg="white"
        border="1px solid"
        borderColor="#DFE1E6"
        borderRadius="sm"
        boxShadow="0 1px 1px 0 rgba(9, 30, 66, 0.08)"
        p={ 6 }
      >
        <LeadForm
          lead={ lead }
          onSubmit={ handleUpdate }
          onCancel={ handleCancel }
          isLoading={ updateMutation.isPending }
        />
      </Box>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={ isDeleteOpen }
        onClose={ onDeleteClose }
        onConfirm={ handleDelete }
        title={t('leads.deleteLead')}
        message={t('leads.deleteLeadConfirm')}
        confirmText={t('common.delete')}
        isLoading={ deleteMutation.isPending }
        colorScheme="red"
      />
    </Box>
  )
}

export default LeadEdit
